/**
 * What a voucher upload (or "Add testers") did: the counts, anyone kept
 * because they have already ordered, the rows that were NOT loaded, and then
 * the rows that WERE loaded but carry a note for HR to look at.
 */
import type { StaffOrderImportResult } from '@/services/api/staffOrderService'
import { cn } from '@/lib/utils'

import { formatCount } from './helpers'

type Issue = { row: number; employee_id: string | null; reason: string }

function IssueTable({ rows, tone }: { rows: Issue[]; tone: 'red' | 'amber' }) {
  return (
    <div
      className={cn(
        'max-h-64 overflow-y-auto rounded-md border bg-white',
        tone === 'red' ? 'border-red-200' : 'border-amber-200',
      )}
    >
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-white text-left text-gray-500">
          <tr className="border-b">
            <th className="px-2 py-1.5 font-medium">Row</th>
            <th className="px-2 py-1.5 font-medium">Employee number</th>
            <th className="px-2 py-1.5 font-medium">Reason</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.row}-${r.employee_id ?? ''}-${i}`} className="border-b last:border-0 align-top">
              <td className="px-2 py-1.5 tabular-nums text-gray-500">{r.row}</td>
              <td className="px-2 py-1.5 tabular-nums">{r.employee_id || '—'}</td>
              <td className="px-2 py-1.5">{r.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function ImportResultView({ result, heading }: { result: StaffOrderImportResult; heading: string }) {
  const warnings = result.warnings ?? []
  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm" role="status">
      <div>
        <p className="font-medium text-gray-900">{heading}</p>
        <p className="text-gray-700">
          Added {formatCount(result.added)} · Updated {formatCount(result.updated)} · Removed{' '}
          {formatCount(result.removed)}
        </p>
      </div>
      {result.kept_with_orders.length > 0 && (
        <p className="text-xs text-gray-600">
          Kept because they have already ordered ({formatCount(result.kept_with_orders.length)}):{' '}
          {result.kept_with_orders.join(', ')}
        </p>
      )}
      {result.rejected.length > 0 && (
        <div className="space-y-1">
          <p className="font-medium text-red-700">Not loaded ({formatCount(result.rejected.length)})</p>
          <p className="text-xs text-red-700">These rows were left out. Fix them in the file and upload it again.</p>
          <IssueTable rows={result.rejected} tone="red" />
        </div>
      )}
      {warnings.length > 0 && (
        <div className="space-y-1">
          <p className="font-medium text-amber-800">Loaded, with a note ({formatCount(warnings.length)})</p>
          <p className="text-xs text-amber-800">
            These people were loaded and have a voucher. Please look at each note.
          </p>
          <IssueTable rows={warnings} tone="amber" />
        </div>
      )}
    </div>
  )
}
